/**
 * @ngdoc controllers
 * @memberof mercury.modules.time
 * @name TimeClockStaffreportCtrl
 */
angular.module('mercury.modules.time.clock')
.controller('TimeClockStaffreportCtrl', ["reporttime", "$filter", "$scope", "$q", "TimeStaff", "TimeRoles", "TimeClock", "title", "logger", "PayRules", "PayRuleConstant", "userinfo", "dialog", "localStorageService", function(reporttime, $filter, $scope,
  $q, TimeStaff, TimeRoles, TimeClock, title, logger, PayRules, PayRuleConstant, userinfo, dialog, localStorageService) {

  title.set('Punches By Staff');

  $scope.activePackages = [];
  $scope.loadActivePackages = true;
  $scope.hasPayrollPermission = userinfo.companyHasPermission('Payroll');

  /**
   * Action handler for processing a request to export timeclock data to the
   * format required the chosen payroll provider.
   *
   * @param {*} format
   */
  $scope.export = function(format) {
    TimeClock.downloadExport(reporttime.start(), reporttime.end(), format);
  };

  /**
   * Internal helper to calculate the adjusted pay rate for a specific
   * rule type rounded to 2 decimal places.
   *
   * Rule types are:
   *   0 - add the dollar amount onto base rate
   *   1 - subtract the dollar amount from base rate
   *   2 - equals - use this directly as the rate
   *   3 - multiple - multiply the base rate by this
   *   4 - percentageTotal - multiply by the adjusted rate
   *
   * @param {*} baseRate
   * @param {*} rule
   * @returns
   */
  function calculateRuleRate(baseRate, rule) {
    var ruleRate = 0;
    switch (rule.ruleType) {
      case 0:
      case PayRuleConstant.RuleType.Add:
        ruleRate = rule.ruleRate;
      break;
      case 1:
      case PayRuleConstant.RuleType.Subtract:
        ruleRate = (rule.ruleRate) * -1;
      break;
      case 2:
      case PayRuleConstant.RuleType.Equals:
        ruleRate = rule.ruleRate;
      break;
      case 3:
      case PayRuleConstant.RuleType.Multiple:
        ruleRate = (baseRate * rule.ruleRate) - baseRate;
      break;
      case 4:
      case PayRuleConstant.RuleType.PercentageTotal:
        ruleRate = rule.ruleRate;
      break;
      default:
        logger.warn('ignoring unknown rule type ' + rule.ruleType);
      break;
    }
    return Math.round(ruleRate * 100) / 100;
  }

  var getOverrideIsDeleted = function(override) {
    return !!override.deletedAt;
  };

  /**
   * Decide whether to strike through an rule to show it is not currently part of calculations
   */
  var getIsRuleDisabled = function(rule) {
    return !(
      (rule.override === null) ||
      (rule.override.type === PayRuleConstant.OverrideType.Add && !getOverrideIsDeleted(rule.override)) ||
      (rule.override.type === PayRuleConstant.OverrideType.Deactivate && getOverrideIsDeleted(rule.override)) ||
      (rule.override.type === PayRuleConstant.OverrideType.TimeAdjustment && !getOverrideIsDeleted(rule.override))
      );
  };

  /**
   * Internal function that populates the $scope variables with objects and
   * arrays used when rendering the template.
   *
   * $scope.staff - an array of staff member objects with detail of the shifts
   *                they've worked
   * $scope.totals.rules - an object holding total hours and wage amounts
   *                             for each rule type
   * $scope.totals.roles - an object holding total hours and wage amounts for
   *                       each role
   *
   * Note that it'd make more sense to use an object keyed by staff member id
   * here, however we need to have the same scope variables as the TimeClockListCtrl
   * controller with exactly the same structure as we want to leverage the
   * `inputpayrate` directive which is tightly coupled to the scope structure
   * used by the TimeClockListCtrl code.
   *
   * @param {*} clock
   */
  var bindClockDataForDisplay = function(clock) {

    var ordinaryTimeName = 'Ordinary Time';

    var staffMember = $filter('filter')($scope.staff, {id: clock.staffMemberId})[0];

    if (!staffMember) {
      // copy main staff data from staff member API response
      staffMember = $filter('filter')($scope.allStaff, {id: clock.staffMemberId})[0];
      staffMember.paycode = clock.staffMemberPayCode;
      staffMember.totalLength = 0;
      staffMember.totalPay = 0;
      staffMember.clocks = [];
      staffMember.typeTotals = {
        'Ordinary Time': {
          hours: 0,
          amount: 0,
          rate: clock.hourlyRate,
          disabled: true
        }
      };

      // and push the new object to our list used for rendering
      $scope.staff.push(staffMember);
    }

    if (!$scope.totals.rules[ordinaryTimeName]) {
      $scope.totals.rules[ordinaryTimeName] = {
        hours: 0,
        amount: 0
      };
    }

    if (!$scope.totals.roles[clock.roleId]) {
      $scope.totals.roles[clock.roleId] = {
        name: clock.roleName,
        totalPay: 0,
        totalLength: 0,
        staff: {}
      };
    }

    if (!$scope.totals.roles[clock.roleId].staff[clock.staffMemberId]) {
      $scope.totals.roles[clock.roleId].staff[clock.staffMemberId] = {
        name: clock.staffMemberFirstName + ' ' + (clock.staffMemberLastName || ''),
        totalPay: 0,
        totalLength: 0
      };
    }

    clock.hasRules = clock.rules && clock.rules.length > 0;
    clock.hasNonDisabledRules = clock.rules && clock.rules
      .reduce(function(all, rule) {
        return all || !getIsRuleDisabled(rule);
      }, false);

    // keep track of whether or not the staff member has any clockins that
    // contain rules that aren't disabled
    staffMember.hasNonDisabledRules =
      staffMember.hasNonDisabledRules || clock.hasNonDisabledRules;

    var hasFixedRateRule = false;

    // Process rules for the clock, updating both staff member and overall
    // running totals appropriately.
    angular.forEach(clock.rules, function(rule) {
      var ruleDisplayName = 'Adjustment - ' + rule.ruleName;
      if (!staffMember.typeTotals[ruleDisplayName]) {
        staffMember.typeTotals[ruleDisplayName] = {
          hours: 0,
          amount: 0,
          rate: calculateRuleRate(clock.hourlyRate, rule),
          ruleType: rule.ruleType
        };
      }

      // decide should we use the rule in calculations
      var disabledRule = getIsRuleDisabled(rule);

      if (!disabledRule) {
        var ruleRate = calculateRuleRate(clock.hourlyRate, rule);

        staffMember.typeTotals[ruleDisplayName].hours += rule.length;
        // total paid hours can be rounded up depending on a bool set by the user so use this field when doing the cost calculation to take rounding into account
        staffMember.typeTotals[ruleDisplayName].amount += rule.processedRuleCost != null ? rule.processedRuleCost : (ruleRate * rule.totalPaidHours);

        staffMember.typeTotals[ruleDisplayName].disabled = disabledRule;

        // add source so we can display the correct icons for the rules
        // use rule source unless it is an override
        var ruleSource = (rule.override && rule.override.type === PayRuleConstant.OverrideType.Add) ?
          PayRuleConstant.RuleSource.Override :
          rule.source;
        staffMember.typeTotals[ruleDisplayName].source = ruleSource;

        if (!$scope.totals.rules[ruleDisplayName] && !disabledRule) {
          $scope.totals.rules[ruleDisplayName] = {
            hours: 0,
            amount: 0,
            ruleType: rule.ruleType,
            rate: rule.ruleRate
          };
        }

        $scope.totals.rules[ruleDisplayName].hours += rule.length;
        // total paid hours can be rounded up depending on a bool set by the user so use this field when doing the cost calculation to take rounding into account
        $scope.totals.rules[ruleDisplayName].amount += rule.processedRuleCost != null ? rule.processedRuleCost : (ruleRate * rule.totalPaidHours);
      }

      hasFixedRateRule = hasFixedRateRule || rule.ruleType === PayRuleConstant.RuleType.Equals;
    });

    staffMember.totalLength += clock.length;
    staffMember.totalPay += clock.lengthUnrounded * clock.adjustedHourlyRate;
    staffMember.clocks.push(clock);

    // Update role and overall rolling totals.

    // We don't update the ordinary time rolling totals if there is an rule
    // of type 2 appled (that specifies a fixed rate).
    if (!hasFixedRateRule) {
      staffMember.typeTotals[ordinaryTimeName].hours += clock.lengthUnrounded;
      staffMember.typeTotals[ordinaryTimeName].amount += clock.lengthUnrounded * clock.hourlyRate;
      // If the staff member has any clockins that have non-disabled rules
      // present then we want to display the ordinary time total.
      if (staffMember.typeTotals[ordinaryTimeName].disabled) {
        staffMember.typeTotals[ordinaryTimeName].disabled = !clock.hasNonDisabledRules;
      }
      $scope.totals.rules[ordinaryTimeName].hours += clock.lengthUnrounded;
      $scope.totals.rules[ordinaryTimeName].amount += (clock.lengthUnrounded * clock.hourlyRate);
    }

    $scope.totals.roles[clock.roleId].totalLength += clock.lengthUnrounded;
    $scope.totals.roles[clock.roleId].totalPay += clock.lengthUnrounded * clock.adjustedHourlyRate;

    $scope.totals.roles[clock.roleId].staff[clock.staffMemberId].totalLength += clock.lengthUnrounded;
    $scope.totals.roles[clock.roleId].staff[clock.staffMemberId].totalPay += clock.lengthUnrounded * clock.adjustedHourlyRate;

    $scope.totals.totalLength += clock.lengthUnrounded;
    $scope.totals.totalPay += clock.lengthUnrounded * clock.adjustedHourlyRate;

  };

  /**
   * Internal function that will load data for the report from various API calls,
   * and then create the required data structure for the template to render
   * succesfully.
   */
  var loadReportData = function() {

    $scope.totals = {
      totalPay: 0,
      totalLength: 0,
      roles: {},
      rules: {}
    };

    $scope.staff = [];

    var loadingPromises = {
      // Note it's important we get staff member and roles that were active
      // in the reporting period, not just the currently active results.
      staffMembers: TimeStaff.getAll(reporttime.end()),
      staffRoles: TimeRoles.getAll(),
      allClocks: TimeClock.getAllActive(reporttime.start(), reporttime.end())
    };

    $scope.loading = $q.all(loadingPromises);
    $scope.loading.then(function(result) {

      $scope.roles = result.staffRoles;
      $scope.allStaff = result.staffMembers;
      var effectiveClocks = TimeClock.effectiveClocks(result.allClocks, reporttime.start(), reporttime.end());

      angular.forEach(effectiveClocks, function(clock) {
        bindClockDataForDisplay(clock);
      });
      $scope.hasClockins = effectiveClocks.length > 0;

      // keep loading rules
      if ($scope.hasPayrollPermission) {
        $scope.loadingRules = PayRules.getAll();
        $scope.loadingRules.then(function(result) {
          $scope.activePackages = $filter('filter')(result, function(package) {
            return package.activation != null;
          });
          $scope.loadActivePackages = false;
        });
      } else {
        $scope.loadActivePackages = false;
      }
    });
  };

  /**
   * we only want to show the override icon if the rule has been manually added
   */
  $scope.getRuleIcon = function(rule) {
    if (rule.override && rule.override.type === PayRuleConstant.OverrideType.Add) {
      return PayRuleConstant.RuleTypeIcon.Override;
    } else {
      return PayRuleConstant.RuleTypeIcon[rule.source];
    }
  };

  $scope.getRuleIconTooltip = function(rule) {
    if (rule.override && rule.override.type === PayRuleConstant.OverrideType.Add) {
      return PayRuleConstant.RuleTypeIconTooltip.Override;
    } else {
      return PayRuleConstant.RuleTypeIconTooltip[rule.source];
    }
  };

  $scope.payrollDialog = function() {
    var dontShow = localStorageService.get('dismissFlag.payrollDetailsDialog');
    var hasActivePackages = $scope.activePackages.length > 0;

    // If user has selected the 'don't show me again' option or has not active packages then dont open dialog
    if (dontShow || !hasActivePackages) {
      return;
    }

    dialog.open({
      size: 'md',
      templateUrl: 'dialogs/payrolldetails/payrolldetailsdialog.tpl.html',
      controller: 'PayrollDetailsDialogCtrl'
    });
  };

  /**
   * Action handler used for the save button in the `inputpayrate` directive
   * markup.
   *
   * @param {*} clock
   */
  $scope.saveClock = function(clock) {

    $scope.clockSaving = clock.id;
    $scope.clockSavePromise = TimeClock.update(clock);
    $scope.clockSavePromise.then(function(savedClock) {
      clock.id = savedClock.id;
      clock.clockinTime = savedClock.clockinTime;
      clock.clockoutTime = savedClock.clockoutTime;
      clock.isCompanyClockin = savedClock.isCompanyClockin;
      clock.adjustedHourlyRate = savedClock.adjustedHourlyRate;
      clock.rules = savedClock.rules;
      clock.length = TimeClock.getClockinLength(clock.clockoutTime, clock.clockinTime);
      clock.lengthUnrounded = TimeClock.getClockinLengthUnrounded(clock.clockoutTime, clock.clockinTime);
      $scope.clockSaving = null;

      // We need to refresh/rebuild the report data - it's too complex to try
      // and unpick the clockin that was updated and apply a delta. We could
      // try and only load the clocks data here and rebuild the data with the
      // existing staff/roles data, but for now we'll just trigger a normal
      // page load data retrieval as it's far simpler.
      loadReportData();
    });
  };

  // Immediately load data on initial load.
  loadReportData();
}]);
